import React from "react"
import { Card, CardContent, CardTitle, CardTitleActions } from "~/react/ui/card"
import { LeadSignalItem, type EventType } from "./lead-signal-item"
import type DashboardActivity from "@/types/serializers/DashboardActivity"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/react/ui/collapsible"
import { Icon } from "../ui/icon"
import { LoadingSpinner } from "../ui/loading-spinner"

type DomainSignalsProps = {
  domain: { id?: number; value: string; company_name?: string }
}

type ApiResponse = {
  domain_events: DashboardActivity[]
  summary: { summary: string; key_movements: boolean }
}

function eventToMessage(event: DashboardActivity): {
  message: string
  type: EventType
} | null {
  const meta = (event as any).associated_record_data as any
  switch (event.event_type as EventType) {
    case "email_added":
      return {
        message: `${meta?.company_name || meta?.value || "This company"} has new email addresses`,
        type: "email_added",
      }
    case "technology_added":
      return {
        message: `${meta?.company_name || meta?.value || "This company"} added new technologies`,
        type: "technology_added",
      }
    case "technology_deleted":
      return {
        message: `${meta?.company_name || meta?.value || "This company"} removed some technologies`,
        type: "technology_deleted",
      }
    case "linkedin_job_added":
      return {
        message: `${meta?.company_name || meta?.value || "This company"} published new job openings`,
        type: "linkedin_job_added",
      }
    case "funding_added":
      return {
        message: `${meta?.company_name || "This company"} raised ${meta?.type || "a"} funding round`,
        type: "funding_added",
      }
    case "executive_added":
      return {
        message: `${meta?.company_name || "This company"} added a new executive`,
        type: "executive_added",
      }
    case "spike_metric":
      return {
        message: `${meta?.company_name || "This company"} had a spike in activity`,
        type: "spike_metric",
      }
    case "position_changed":
      return {
        message: `${meta?.full_email || "A contact"} changed position`,
        type: "position_changed",
      }
    case "location_changed":
      return {
        message: `${meta?.full_email || "A contact"} changed location`,
        type: "location_changed",
      }
    default:
      return null
  }
}

export default function DomainSignals({ domain }: DomainSignalsProps) {
  const [data, setData] = React.useState<ApiResponse | null | undefined>(undefined)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [isOpen, setIsOpen] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (!domain?.value) return
    setIsLoading(true)
    fetch(`/search/${encodeURIComponent(domain.value)}/events`, {
      headers: {
        Accept: "application/json",
      },
    })
      .then((r) => r.json())
      .then((data: ApiResponse) => {
        setData(data)
      })
      .catch(() => {
        setData(null)
      })
      .finally(() => setIsLoading(false))
  }, [domain?.value])

  const signals = data?.domain_events
    .map((e) => {
      const parsed = eventToMessage(e)
      if (!parsed) return null
      return {
        id: e.id,
        timestamp: new Date(e.created_at),
        eventType: parsed.type as EventType,
        message: parsed.message,
      }
    })
    .filter((e) => e !== null) as {
    id: number
    timestamp: Date
    eventType: EventType
    message: string
  }[]

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card density="compact" className="mb-4">
        <CollapsibleTrigger asChild>
          <CardTitle className="accordion__header">
            <h2 className="accordion__title">Signals</h2>
            <CardTitleActions>
              <button className="btn-empty accordion__expand">
                {isOpen ? <Icon icon="angle-up" /> : <Icon icon="angle-down" />}
              </button>
            </CardTitleActions>
          </CardTitle>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {data === undefined && (
            <CardContent className="py-2 px-4 border-top">
              <LoadingSpinner />
            </CardContent>
          )}

          {data === null && (
            <CardContent className="py-2 px-4 border-top">No recent signals for this company</CardContent>
          )}

          {data !== null && data !== undefined && (
            <CardContent className="border-top">
              {data?.summary?.key_movements && (
                <Card variant="signal" density="relaxed" className="p-2">
                  <CardTitle className="px-4 pt-4">
                    <Icon icon="sparkle" className="mr-2 text-ai" />
                    Summary
                  </CardTitle>
                  <CardContent className="py-2 px-4">{data?.summary?.summary}</CardContent>
                </Card>
              )}

              {/* List of signals */}
              <div className="p-2 text-xs-medium text-hint">{signals.length} updates in past 90 days</div>
              <div className="d-flex flex-column gap-2" style={{ maxHeight: "600px", overflowY: "auto" }}>
                {signals.length === 0 && !isLoading && (
                  <span className="text-sm grey">No recent signals for this company</span>
                )}

                {signals.map((signal) => (
                  <LeadSignalItem
                    key={signal.id}
                    message={signal.message}
                    timestamp={signal.timestamp}
                    eventType={signal.eventType}
                    entityType="domain"
                    count={1}
                  />
                ))}
              </div>
            </CardContent>
          )}
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
